import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentPeriod, fmtDate, fmtTime, periodLabel, type ID } from '@hrms/shared';
import { api } from '../../../lib/api';
import { addMonths, daysInMonth, todayISO } from '../../../lib/date';
import { useMut } from '../../../lib/query';
import { EmployeePicker } from '../components/EmployeePicker';
import { PeriodSelect } from '../components/misc';
import { Alert, Badge, Button, Card, Columns, ConfirmDialog, DataTable, DateField, Grid, Modal, PageHeader, Row, Screen, Stack, Text } from '../../../ui';

/** Buttons default to alignSelf flex-start; bottom-align them with labelled fields. */
const END = { alignSelf: 'flex-end' as const };

/** Process punches into attendance days, then lock months so LOP flows into payroll. */
export function AttendanceLockScreen() {
  const locks = useQuery({ queryKey: ['attendance', 'locks'], queryFn: () => api.attendance.locks() });
  const [period, setPeriod] = useState(addMonths(currentPeriod(), -1));
  const [confirm, setConfirm] = useState(false);
  const [unlock, setUnlock] = useState<ID | null>(null);
  const [proc, setProc] = useState({ date_from: `${currentPeriod()}-01`, date_to: todayISO(), employee_id: null as ID | null });
  const lock = useMut(() => api.attendance.lock(period), {
    invalidate: [['attendance']],
    success: (r) => `${periodLabel(r.period)} locked · ${r.inputs_generated} payroll inputs generated`,
    onSuccess: () => setConfirm(false),
  });
  const doUnlock = useMut((id: ID) => api.attendance.unlock(id), { invalidate: [['attendance']], success: 'Period unlocked', onSuccess: () => setUnlock(null) });
  const process = useMut(() => api.attendance.process({ date_from: proc.date_from, date_to: proc.date_to, employee_id: proc.employee_id ?? undefined }), {
    invalidate: [['attendance']],
    success: (r) => `${r.processed} attendance days processed`,
  });
  const already = locks.data?.some((l) => l.period === period);
  const rangeBad = proc.date_from > proc.date_to;
  return (
    <Screen testID="attendance-lock">
      <PageHeader title="Attendance processing & lock" subtitle="Process punches into attendance days, then lock the month to push LOP into payroll inputs." />
      <Columns ratio={[1, 1]}>
        <Stack>
          <Card title="1. Process attendance" subtitle="Recalculate status, late marks and OT from punches, leave and holidays.">
            <Stack gap={14}>
              <Grid min={180} gap={14}>
                <DateField label="From" value={proc.date_from} max={todayISO()} onChange={(date_from) => setProc({ ...proc, date_from })} />
                <DateField label="To" value={proc.date_to} max={todayISO()} error={rangeBad ? 'Must be on or after From' : undefined} onChange={(date_to) => setProc({ ...proc, date_to })} />
              </Grid>
              <EmployeePicker label="Employee (optional)" placeholder="All employees" value={proc.employee_id} onChange={(employee_id) => setProc({ ...proc, employee_id })} />
              <Row justify="flex-end">
                <Button icon="refresh" title="Process" disabled={rangeBad} loading={process.isPending} onPress={() => process.mutate(undefined)} testID="attendance-process" />
              </Row>
            </Stack>
          </Card>
          <Card title="2. Lock month" subtitle="Locked days cannot be regularised; LOP flows to payroll inputs.">
            <Row align="flex-end">
              <View style={{ minWidth: 200 }}>
                <PeriodSelect value={period} onChange={setPeriod} />
              </View>
              <Button variant="primary" icon="lock" disabled={already} title={already ? 'Already locked' : `Lock ${periodLabel(period)}`} onPress={() => setConfirm(true)} testID="attendance-lock-btn" style={END} />
            </Row>
            <Text variant="small" style={{ marginTop: 8 }}>
              {fmtDate(`${period}-01`)} to {fmtDate(`${period}-${daysInMonth(period)}`)}
            </Text>
          </Card>
        </Stack>
        <Card title="Locked periods">
          <DataTable
            rows={[...(locks.data ?? [])].sort((a, b) => b.period.localeCompare(a.period))}
            loading={locks.isLoading}
            emptyTitle="No locked periods"
            columns={[
              { key: 'period', title: 'Period', width: 130, render: (l) => periodLabel(l.period), sortValue: (l) => l.period },
              { key: 'locked_at', title: 'Locked', width: 150, render: (l) => `${fmtDate(l.locked_at)} ${fmtTime(l.locked_at)}`, sortValue: (l) => l.locked_at },
              { key: 'status', title: '', width: 80, sortable: false, render: () => <Badge tone="success">Locked</Badge> },
              { key: 'act', title: '', width: 100, flex: 0, sortable: false, render: (l) => <Button size="sm" variant="ghost" icon="key" title="Unlock" onPress={() => setUnlock(l.id)} /> },
            ]}
          />
        </Card>
      </Columns>
      <Modal
        open={confirm}
        onClose={() => setConfirm(false)}
        title={`Lock ${periodLabel(period)}?`}
        width={440}
        footer={
          <>
            <Button title="Cancel" onPress={() => setConfirm(false)} />
            <Button variant="primary" title="Lock period" loading={lock.isPending} onPress={() => lock.mutate(undefined)} testID="attendance-lock-confirm" />
          </>
        }
      >
        <Alert tone="warning">Pending regularisations for this month will no longer apply. Make sure attendance has been processed.</Alert>
      </Modal>
      <ConfirmDialog open={!!unlock} onClose={() => setUnlock(null)} onConfirm={() => unlock && doUnlock.mutate(unlock)} loading={doUnlock.isPending} title="Unlock period?" message="Payroll inputs generated from this lock may need to be regenerated." confirmLabel="Unlock" danger />
    </Screen>
  );
}
